/**
 * Who placed a category tag, how, and when.
 *
 * Accreditors ask whether a tag reflects human judgment or automated
 * inference, and "a spark icon" does not answer that. Every tag carries a
 * record: set by a person directly, suggested by Leo and approved, or
 * suggested by Leo and edited. Leo never writes a tag on its own, so there is
 * no "unreviewed AI" origin: a suggestion is either approved or it is not on
 * the question.
 */

export type CategoryTagOrigin = "human" | "ai-approved" | "ai-edited"

export type CategoryTagProvenance = {
  origin: CategoryTagOrigin
  /** Display name of the person who placed or approved it. */
  by: string
  /** ISO timestamp. */
  at: string
  /**
   * Program standards carry accreditation weight, so a tag a faculty member
   * places waits for the director before it counts as verified. Absent means
   * verified (content tags, and anything the director placed).
   */
  review?: "pending" | "verified"
}

export type CategoryProvenanceMap = Record<string, CategoryTagProvenance>

/**
 * The map after the question's tags changed.
 *
 * Tags that stay keep their record. A tag that goes is dropped. A tag that
 * arrives is stamped now: from Leo's suggestion if it is listed in \`aiIds\`,
 * otherwise as the author's own choice.
 */
export function stampCategoryChange(
  previousIds: readonly string[],
  nextIds: readonly string[],
  previous: CategoryProvenanceMap | undefined,
  context: {
    by: string
    at?: string
    aiIds?: readonly string[]
    /** Tags that need the director's check when someone else places them. */
    needsReview?: (categoryId: string) => boolean
  },
): CategoryProvenanceMap {
  const before = new Set(previousIds)
  const ai = new Set(context.aiIds ?? [])
  const at = context.at ?? new Date().toISOString()
  const next: CategoryProvenanceMap = {}
  for (const id of nextIds) {
    const kept = previous?.[id]
    if (kept && before.has(id)) {
      next[id] = kept
      continue
    }
    next[id] = {
      origin: ai.has(id) ? "ai-approved" : "human",
      by: context.by,
      at,
      ...(context.needsReview?.(id) ? { review: "pending" as const } : {}),
    }
  }
  return next
}

export function describeProvenance(record: CategoryTagProvenance): {
  origin: string
  status: string
} {
  switch (record.origin) {
    case "ai-approved":
      return { origin: "Suggested by Leo, approved by a person", status: record.review === "pending" ? "Pending review" : "Verified" }
    case "ai-edited":
      return { origin: "Suggested by Leo, edited by a person", status: record.review === "pending" ? "Pending review" : "Verified" }
    default:
      return { origin: "Set by a person", status: record.review === "pending" ? "Pending review" : "Verified" }
  }
}
